import React from "react";
import { Alert, Box, Button, Chip, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FaArrowRight } from "react-icons/fa";
import { estiloCabecalhoInterno, estiloCelulaInterna } from "../utils/societarioEstilos";

// ==========================================================
// ABA FLUXO (seção 3.1 / regra 5)
// Avança na ordem cadastrada em Etapas do Fluxo, somente pelo
// responsável da etapa atual e com documento anexado nela.
// ==========================================================

const dataHora = (v) => (v ? new Date(v).toLocaleString("pt-BR") : "—");

export default function SocietarioFluxo({ alteracao, etapasAtivas, nomeEtapa, situacaoAvanco, onAvancar, alteracaoSalva }) {
  const indiceAtual = etapasAtivas.findIndex((e) => e.id === alteracao.etapaId);
  const etapaAtual = etapasAtivas[indiceAtual];
  const proxima = etapasAtivas[indiceAtual + 1];
  const historico = [...(alteracao.historico || [])].reverse();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Stepper
        activeStep={indiceAtual}
        alternativeLabel
        sx={{
          "& .MuiStepLabel-label": { color: "var(--ig-t-888888) !important", fontSize: "0.8rem" },
          "& .MuiStepLabel-label.Mui-active": { color: "#1998a8 !important", fontWeight: "bold" },
          "& .MuiStepLabel-label.Mui-completed": { color: "var(--ig-t-cccccc) !important" },
          "& .MuiStepIcon-root": { color: "#444" },
          "& .MuiStepIcon-root.Mui-active": { color: "#1998a8" },
          "& .MuiStepIcon-root.Mui-completed": { color: "#2e7d32" },
        }}
      >
        {etapasAtivas.map((e) => (
          <Step key={e.id}>
            <StepLabel>{e.nome}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {etapaAtual ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Typography sx={{ color: "var(--ig-t-cccccc)" }}>Etapa atual:</Typography>
          <Chip label={etapaAtual.nome} sx={{ backgroundColor: "#1998a8", color: "#fff", fontWeight: "bold" }} />
          <Typography sx={{ color: "var(--ig-t-cccccc)", ml: 2 }}>Responsáveis:</Typography>
          {etapaAtual.usuarios.length ? (
            etapaAtual.usuarios.map((u) => (
              <Chip
                key={u.id}
                size="small"
                label={u.nome}
                sx={{ backgroundColor: "var(--ig-f-2a2a2a)", color: "var(--ig-t-ffffff)", border: "1px solid var(--ig-b-444444)" }}
              />
            ))
          ) : (
            <span style={{ color: "#ed6c02" }}>Sem responsável definido em Etapas do Fluxo</span>
          )}
        </Box>
      ) : (
        <Alert severity="warning">A etapa desta alteração não está entre as etapas ativas do fluxo.</Alert>
      )}

      {alteracaoSalva && proxima && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<FaArrowRight />}
            disabled={!situacaoAvanco.permitido}
            onClick={onAvancar}
            sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
          >
            Avançar para {proxima.nome}
          </Button>
          {!situacaoAvanco.permitido && <Typography sx={{ color: "#ed6c02", fontSize: "0.85rem" }}>{situacaoAvanco.motivo}</Typography>}
        </Box>
      )}
      {alteracaoSalva && !proxima && etapaAtual && <Alert severity="success">Alteração na etapa final do fluxo ({etapaAtual.nome}).</Alert>}
      {!alteracaoSalva && <Alert severity="info">Salve a alteração para poder avançar de etapa.</Alert>}

      <Box>
        <Typography variant="subtitle2" sx={{ color: "var(--ig-t-cccccc)", fontWeight: 600, mb: 1 }}>
          Histórico do fluxo
        </Typography>
        <Table size="small" sx={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={estiloCabecalhoInterno}>Data</TableCell>
              <TableCell sx={estiloCabecalhoInterno}>De</TableCell>
              <TableCell sx={estiloCabecalhoInterno}>Para</TableCell>
              <TableCell sx={{ ...estiloCabecalhoInterno, width: "40%" }}>Usuário</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historico.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  sx={{ borderBottom: "1px solid var(--ig-b-333333)", color: "var(--ig-t-666666)", fontStyle: "italic", py: 1.5, textAlign: "center" }}
                >
                  Sem movimentações no fluxo.
                </TableCell>
              </TableRow>
            )}
            {historico.map((h, i) => (
              <TableRow key={i} sx={{ "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" } }}>
                <TableCell sx={{ ...estiloCelulaInterna, whiteSpace: "nowrap", color: "var(--ig-t-cccccc)" }}>{dataHora(h.data)}</TableCell>
                <TableCell sx={estiloCelulaInterna}>{h.deEtapaId ? nomeEtapa(h.deEtapaId) : "—"}</TableCell>
                <TableCell sx={estiloCelulaInterna}>{nomeEtapa(h.paraEtapaId)}</TableCell>
                <TableCell sx={{ ...estiloCelulaInterna, color: "var(--ig-t-cccccc)" }}>
                  {h.usuarioNome}
                  {h.observacao ? <span style={{ color: "var(--ig-t-888888)" }}> — {h.observacao}</span> : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
